import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { downloadFile, downloadFilesAsZip } from "src/utils";

type FileDownloadVariables<T extends File | File[]> = {
  getFileData: () => Promise<T>;
  zipName?: T extends File[] ? string : never;
};

export const useFileDownload = <T extends File | File[] = File>() => {
  const [lastVariables, setLastVariables] = useState<FileDownloadVariables<T> | null>(null);

  const mutation = useMutation({
    mutationFn: async (variables: FileDownloadVariables<T>) => {
      setLastVariables(variables);

      const fileData = await variables.getFileData();

      if (Array.isArray(fileData)) {
        return fileData.length > 1 ? downloadFilesAsZip(fileData, variables.zipName) : downloadFile(fileData[0]);
      }

      return downloadFile(fileData);
    }
  });

  const retry = () => {
    if (lastVariables) {
      mutation.mutate(lastVariables);
    }
  };

  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    retry
  };
};
